import { get, post, put, del } from './client';
import type {
  GtmItemsResponse,
  GtmCalendarResponse,
  GtmFilters,
  GtmStats,
  GtmItem,
  GtmLinkedData,
  GtmEmailContext,
  MarketingBrief,
  GtmFilterParams,
  GtmCalendarDateField,
  GtmSortField,
  MarketingBriefStatus,
  EukaBriefSettings,
  EukaBriefUpdatePayload,
} from '@/types/gtm';

// Credentials
export interface GtmCredentials {
  credentials_json?: string;
  sheet_id?: string;
  sheet_name?: string;
  clear_credentials?: boolean;
}

export interface GtmCredentialsResponse {
  gtm_configured: boolean;
  gtm_sheet_id: string | null;
  gtm_sheet_name: string | null;
  gtm_last_synced_at: string | null;
}

export async function updateGtmCredentials(
  teamId: string,
  credentials: GtmCredentials
): Promise<GtmCredentialsResponse> {
  return post(`/api/teams/${teamId}/gtm/credentials`, credentials);
}

export async function testGtmConnection(
  teamId: string
): Promise<{ status: string; title: string; sheets: string[]; sheet_count: number }> {
  return get(`/api/teams/${teamId}/gtm/test`);
}

// Sync
export interface GtmSyncResponse {
  status: 'complete' | 'scheduled';
  items_fetched?: number;
  items_added?: number;
  items_updated?: number;
  errors?: number;
  message?: string;
  last_synced_at: string | null;
}

export async function triggerGtmSync(
  teamId: string,
  options?: { wait?: boolean }
): Promise<GtmSyncResponse> {
  const params = new URLSearchParams();
  if (options?.wait !== undefined) {
    params.set('wait', options.wait.toString());
  }
  const query = params.toString();
  return post(`/api/teams/${teamId}/gtm/sync${query ? `?${query}` : ''}`);
}

// Items
export async function getGtmItems(
  teamId: string,
  filters?: GtmFilterParams,
  pagination?: { limit?: number; offset?: number },
  sort?: { sort_by?: GtmSortField; sort_order?: 'asc' | 'desc' }
): Promise<GtmItemsResponse> {
  const params = new URLSearchParams();
  if (pagination?.limit) params.set('limit', pagination.limit.toString());
  if (pagination?.offset) params.set('offset', pagination.offset.toString());
  if (filters?.department) params.set('department', filters.department);
  if (filters?.capsule) params.set('capsule', filters.capsule);
  if (filters?.is_new !== undefined) params.set('is_new', filters.is_new.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  if (filters?.date_type) params.set('date_type', filters.date_type);
  if (sort?.sort_by) params.set('sort_by', sort.sort_by);
  if (sort?.sort_order) params.set('sort_order', sort.sort_order);
  const query = params.toString();
  return get(`/api/teams/${teamId}/gtm/items${query ? `?${query}` : ''}`);
}

export async function getGtmItem(
  teamId: string,
  itemId: string
): Promise<GtmItem> {
  return get(`/api/teams/${teamId}/gtm/items/${itemId}`);
}

export async function getGtmItemLinked(
  teamId: string,
  itemId: string
): Promise<GtmLinkedData> {
  return get(`/api/teams/${teamId}/gtm/items/${itemId}/linked`);
}

export async function getGtmEmailContext(
  teamId: string,
  params: { itemId?: string; productId?: string }
): Promise<GtmEmailContext> {
  const searchParams = new URLSearchParams();
  if (params.itemId) searchParams.set('item_id', params.itemId);
  if (params.productId) searchParams.set('product_id', params.productId);
  const query = searchParams.toString();
  return get(`/api/teams/${teamId}/gtm/email-context${query ? `?${query}` : ''}`);
}

// Calendar
export async function getGtmCalendar(
  teamId: string,
  params?: {
    date_type?: GtmCalendarDateField;
    department?: string;
    capsule?: string;
    is_new?: boolean;
    search?: string;
    weeks_back?: number;
    weeks_forward?: number;
  }
): Promise<GtmCalendarResponse> {
  const searchParams = new URLSearchParams();
  if (params?.date_type) searchParams.set('date_type', params.date_type);
  if (params?.department) searchParams.set('department', params.department);
  if (params?.capsule) searchParams.set('capsule', params.capsule);
  if (params?.is_new !== undefined) searchParams.set('is_new', params.is_new.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.weeks_back !== undefined) searchParams.set('weeks_back', params.weeks_back.toString());
  if (params?.weeks_forward !== undefined) searchParams.set('weeks_forward', params.weeks_forward.toString());
  const query = searchParams.toString();
  return get(`/api/teams/${teamId}/gtm/calendar${query ? `?${query}` : ''}`);
}

// Filters & Stats
export async function getGtmFilters(teamId: string): Promise<GtmFilters> {
  return get(`/api/teams/${teamId}/gtm/filters`);
}

export async function getGtmStats(teamId: string): Promise<GtmStats> {
  return get(`/api/teams/${teamId}/gtm/stats`);
}

// Marketing Briefs
export async function getMarketingBriefs(
  teamId: string,
  status?: MarketingBriefStatus
): Promise<{ briefs: MarketingBrief[] }> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const query = params.toString();
  return get(`/api/teams/${teamId}/gtm/briefs${query ? `?${query}` : ''}`);
}

export async function getMarketingBrief(
  teamId: string,
  briefId: string
): Promise<MarketingBrief> {
  return get(`/api/teams/${teamId}/gtm/briefs/${briefId}`);
}

export async function updateMarketingBrief(
  teamId: string,
  briefId: string,
  data: { title?: string; content?: Record<string, unknown>; status?: MarketingBriefStatus }
): Promise<MarketingBrief> {
  return put(`/api/teams/${teamId}/gtm/briefs/${briefId}`, data);
}

export async function deleteMarketingBrief(
  teamId: string,
  briefId: string
): Promise<{ status: string; id: string }> {
  return del(`/api/teams/${teamId}/gtm/briefs/${briefId}`);
}

export async function getEukaBriefSettings(
  teamId: string,
  briefId: string
): Promise<EukaBriefSettings> {
  return get(`/api/teams/${teamId}/gtm/briefs/${briefId}/euka`);
}

export async function updateEukaBrief(
  teamId: string,
  briefId: string,
  payload: EukaBriefUpdatePayload
): Promise<EukaBriefSettings> {
  return put(`/api/teams/${teamId}/gtm/briefs/${briefId}/euka`, payload);
}

// Forward Coverage Waterfall
export interface WaterfallData {
  weeks: string[];
  target_wos: Record<string, number>;
  departments: string[];
  styles: string[];
  scs: string[];
  hierarchy: Record<string, Record<string, {
    name: string;
    scs: string[];
    sc_sizes: Record<string, string[]>;
  }>>;
  positions: Record<string, {
    oh: number;
    dept: string;
    sty: string;
    sc: string;
    sz: string;
    buy: number;
    arr_wi: number;
    decision: string;
    wd: number[];
    po: number[];
    to: number[];
  }>;
}

export async function getForwardCoverage(teamId: string): Promise<WaterfallData> {
  return get(`/api/teams/${teamId}/gtm/forward-coverage`);
}

// Replenishment Dashboard
export interface ReplenishmentPosition {
  sc: string; sz: string; sn: string; ss: string;
  dept: string; sdept: string; tier: string; noos: boolean; vendor: string;
  dec: string; asp: number; lc: number; gm: number;
  ats: number; pipe: number; dal: number; ws: number;
  sa: number; aw: number; need: number; qf: number;
  inv: number; rev: number; roi: number; irr: number;
  cwos: number; awos: number; fwos: number; twos: number;
}

export interface ReplenishmentWFWeek {
  week: string; bop: number; demand: number; inbound: number;
  eop: number; lost_sales: number; wos: number;
}

export interface ReplenishmentWFPosition {
  sc: string; size: string; style_name: string; tier: string; noos: boolean;
  arrival_week: number; baseline: ReplenishmentWFWeek[];
}

export interface ReplenishmentData {
  run_date: string;
  coverage_days: number;
  first_week_adj: number;
  positions: ReplenishmentPosition[];
  waterfall: Record<string, ReplenishmentWFPosition>;
}

export async function getReplenishment(teamId: string): Promise<ReplenishmentData> {
  return get(`/api/teams/${teamId}/gtm/replenishment`);
}
